import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import LockIcon from '@mui/icons-material/Lock';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PHASE_COLORS = [
  '#ec4899', // Pink
  '#f59e0b', // Yellow
  '#3b82f6', // Blue
  '#10b981', // Green
  '#8b5cf6', // Purple
  '#f43f5e', // Rose
  '#0ea5e9'  // Sky
];

export const FoundationRoadmap = ({
  categoryData,
  isLight,
  isPurchased,
  previewIds,
  navigate,
  setAlertMessage,
  setAlertOpen,
  searchQuery,
  getBadgeStyle
}) => {
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/$/, "");
  const z2hProgress = parseInt(localStorage.getItem('foundation_progress') || '1', 10);
  
  // Extract only the Phase groups
  // Dynamically derive all phase groups from catalog — no hardcoding needed
  const phaseGroups = [...new Set(categoryData.topics.map(t => t.group).filter(g => /^Phase\s+\d+$/.test(g)))]
    .sort((a, b) => parseInt(a.replace('Phase ', '')) - parseInt(b.replace('Phase ', '')));
  const totalPhases = phaseGroups.length;
  
  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: '1000px', mx: 'auto', mt: 8, mb: 12 }}>
      {/* Central Animated Dashed Line */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: { xs: 32, md: '50%' },
        transform: { xs: 'none', md: 'translateX(-50%)' },
        width: 4,
        background: `repeating-linear-gradient(to bottom, ${isLight ? '#cbd5e1' : '#3f3f46'} 0, ${isLight ? '#cbd5e1' : '#3f3f46'} 10px, transparent 10px, transparent 20px)`,
        zIndex: 0
      }}>
        {/* Progress Fill Line */}
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: `${Math.min(100, (z2hProgress / totalPhases) * 100)}%` }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          style={{
            width: '100%',
            background: 'linear-gradient(to bottom, #3b82f6, #8b5cf6)',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 6, md: 0 } }}>
        {phaseGroups.map((groupName, phaseIndex) => {
          const query = searchQuery.toLowerCase();
          const groupTopics = categoryData.topics.filter(t =>
            t.group === groupName && (
              !query ||
              t.title.toLowerCase().includes(query) ||
              t.desc.toLowerCase().includes(query) ||
              t.badge.toLowerCase().includes(query)
            )
          );
          
          if (groupTopics.length === 0) return null;
          
          // Phase properties
          const phaseNum = phaseIndex + 1;
          const isPhaseUnlocked = phaseNum <= z2hProgress || phaseNum === 1;
          const color = PHASE_COLORS[phaseIndex % PHASE_COLORS.length];
          const isEven = phaseIndex % 2 === 1;

          return (
            <Box key={groupName} sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: isEven ? 'flex-start' : 'flex-end',
              position: 'relative',
              width: '100%',
              minHeight: { xs: 'auto', md: '280px' },
              pl: { xs: 8, md: 0 } // padding left for mobile to make space for the line
            }}>
              
              {/* Center Milestone Node */}
              <Box sx={{
                position: 'absolute',
                left: { xs: 32, md: '50%' },
                top: { xs: 24, md: '50%' },
                transform: { xs: 'translate(-50%, -50%)', md: 'translate(-50%, -50%)' },
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <motion.div
                  whileHover={isPhaseUnlocked ? { scale: 1.15 } : {}}
                  style={{
                    width: isPhaseUnlocked ? 64 : 48,
                    height: isPhaseUnlocked ? 64 : 48,
                    borderRadius: '50%',
                    backgroundColor: isLight ? '#ffffff' : '#18181b',
                    border: `4px solid ${isPhaseUnlocked ? color : (isLight ? '#cbd5e1' : '#3f3f46')}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isPhaseUnlocked ? `0 0 20px ${color}60` : 'none',
                    transition: 'all 0.3s ease',
                    zIndex: 2
                  }}
                >
                  {!isPhaseUnlocked ? (
                    <LockIcon sx={{ color: isLight ? '#94a3b8' : '#71717a', fontSize: 20 }} />
                  ) : (
                    <Typography sx={{ fontWeight: 900, color, fontSize: '1.5rem' }}>
                      {phaseNum}
                    </Typography>
                  )}
                </motion.div>
                <Typography sx={{ 
                  position: 'absolute', 
                  bottom: -30, 
                  whiteSpace: 'nowrap',
                  fontWeight: 800, 
                  fontSize: '0.75rem', 
                  letterSpacing: '1px', 
                  textTransform: 'uppercase',
                  color: isPhaseUnlocked ? color : (isLight ? '#94a3b8' : '#71717a')
                }}>
                  {groupName}
                </Typography>
              </Box>

              {/* Connecting branch (Only desktop) */}
              <Box sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: '50%',
                left: isEven ? 'calc(50% - 50px)' : 'calc(50% + 32px)',
                right: isEven ? 'calc(50% + 32px)' : 'calc(50% - 50px)',
                height: 0,
                borderTop: `2px dashed ${isPhaseUnlocked ? color : (isLight ? '#cbd5e1' : '#3f3f46')}`,
                zIndex: 1
              }} />

              {/* Content Card */}
              <Box sx={{
                width: { xs: '100%', md: 'calc(50% - 50px)' },
                zIndex: 3
              }}>
                <motion.div
                  initial={{ opacity: 0, x: isEven ? 50 : -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <Paper elevation={0} sx={{
                    p: 3,
                    borderRadius: 4,
                    bgcolor: isLight ? 'rgba(255,255,255,0.8)' : 'rgba(18,18,22,0.8)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${isPhaseUnlocked ? color + '40' : (isLight ? '#e2e8f0' : '#27272a')}`,
                    boxShadow: isPhaseUnlocked 
                      ? (isLight ? `0 20px 40px -10px ${color}15` : `0 20px 40px -10px ${color}20`)
                      : 'none',
                    opacity: isPhaseUnlocked ? 1 : 0.6,
                    filter: isPhaseUnlocked ? 'none' : 'grayscale(0.8)',
                    position: 'relative'
                  }}>
                    {/* Extract phase title from the first topic's desc or parent data if possible */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="h6" fontWeight="900" sx={{ color: isLight ? '#0f172a' : '#f8fafc' }}>
                          {groupTopics[0]?.parentTitle || categoryData.topics.find(t=>t.group === groupName)?.title || `Phase ${phaseNum} Milestones`}
                        </Typography>
                        {phaseNum === 1 && (
                          <Chip
                            label="✦ FREE PREVIEW"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.6rem',
                              fontWeight: 900,
                              letterSpacing: '0.8px',
                              background: `${color}20`,
                              color: color,
                              border: `1px solid ${color}50`,
                              borderRadius: '6px',
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ color: isLight ? '#64748b' : '#a1a1aa', fontSize: '0.85rem' }}>
                         {categoryData.topics.find(t=>t.group === groupName)?.desc || "Master the concepts in this section to proceed."}
                      </Typography>
                    </Box>

                    {/* Sub-Topics / Problems List */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {groupTopics.flatMap(topic => {
                        // Unpack sub-problems
                        if (topic.problems && topic.problems.length > 0) {
                          return topic.problems.map(p => ({ ...p, parentTopicId: topic.id }));
                        }
                        return [topic];
                      }).map((topic, i) => {
                        const isPhase1 = phaseNum === 1;
                        // Phase 1 is always free for everyone
                        // For purchased users: accessible if the phase is unlocked via sequential progress
                        // For non-purchased users: only Phase 1 is accessible
                        const isAccessible = isPhase1 || (isPurchased && isPhaseUnlocked);
                        const badgeStyle = getBadgeStyle(topic.badge || '', isLight);

                        return (
                          <Box
                            key={topic.id + i}
                            onClick={() => {
                                if (!isAccessible) {
                                  if (!isPurchased) {
                                    setAlertMessage('🔒 Locked — Purchase to unlock full access.');
                                  } else {
                                    setAlertMessage('🔒 Complete the current phase first to unlock this one.');
                                  }
                                  setAlertOpen(true);
                                  return;
                                }
                                navigate(`${currentPath}/${topic.id}`);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: isLight ? '#f8fafc' : '#18181b',
                              border: `1px solid ${isLight ? '#e2e8f0' : '#27272a'}`,
                              cursor: isAccessible ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s ease',
                              '&:hover': isAccessible ? {
                                borderColor: color + '60',
                                bgcolor: isLight ? '#ffffff' : '#202026',
                                transform: 'translateX(4px)'
                              } : {}
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                               {!isAccessible && <LockIcon sx={{ fontSize: 13, color: '#64748b' }} />}
                               <Typography sx={{ 
                                 fontSize: '0.85rem', 
                                 fontWeight: 700, 
                                 color: isAccessible ? (isLight ? '#1e293b' : '#e2e8f0') : (isLight ? '#94a3b8' : '#71717a') 
                               }}>
                                 {topic.title}
                               </Typography>
                            </Box>
                            <ChevronRightIcon sx={{ fontSize: 16, color: color, opacity: isAccessible ? 1 : 0 }} />
                          </Box>
                        );
                      })}
                    </Box>

                  </Paper>
                </motion.div>
              </Box>

            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
